import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
    Mail, CalendarCheck, BellRing, RotateCcw, ClipboardList,
    FileText, Globe, Building2, X as XIcon, Send, ChevronDown, ExternalLink,
    ArrowDownLeft,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { apiService } from '../../../services/apiService';
import type {
    Communication, CommunicationDeliveryStatus, CommunicationInboxState, CommunicationKind, Patient,
} from '../../../services/apiService';
import type { Capability } from '../../../config/capabilities';
import { useToast } from '../../../contexts/ToastContext';
import { formatDateTime } from '../../../i18n/locale';
import StatusPill from '../ui/StatusPill';
import type { StatusTone } from '../ui/StatusPill';
import ComposeMailDialog from './ComposeMailDialog';

// Kommunikations-Timeline der Patientenakte (Mini-CRM Phase 1, docs/CRM-PLAN.md).
// Liest ausschließlich aus der communications-Collection; lädt eigenständig
// (Muster: loadInvoices in PatientDetailPage). Visuell die Verallgemeinerung
// der Termin-Mini-Timeline (linksbordierte Event-Liste).

interface CommunicationTimelineProps {
    patientId: number;
    /** Für Compose-Prefill (E-Mail, Sprache, Name). */
    patient: Patient;
    can: (capability: Capability) => boolean;
    /** Hochzählen erzwingt einen Reload (z. B. nach Rechnungsversand aus der Belege-Card). */
    refreshToken?: number;
}

const sectionClass = 'rounded-xl bg-brand-surface border border-brand-border shadow-sm p-5 mb-4';
const labelClass = 'block text-[11px] font-medium uppercase tracking-wider text-brand-text-muted mb-1';

const INITIAL_LIMIT = 10;
// Nachlade-Seite ("Alle anzeigen") — API kappt bei 200.
const PAGE_LIMIT = 200;

// Praxisinterne Mails (gehen an info@, nicht an den Patienten) — per Default
// ausgeblendet, Toggle im Card-Header.
const INTERNAL_KINDS: ReadonlySet<CommunicationKind> = new Set<CommunicationKind>([
    'practice_notification', 'intake_confirmation',
]);

// Absenderadresse je from_account (Spiegel der Server-Transporte:
// emailService = info@, billingMailerService = billing@).
const FROM_ADDRESS: Record<'info' | 'billing', string> = {
    info: 'info@seadent.es',
    billing: 'billing@seadent.es',
};

const KIND_META: Record<CommunicationKind, { icon: LucideIcon; colorClass: string }> = {
    appointment_status: { icon: CalendarCheck, colorClass: 'text-emerald-600' },
    reminder: { icon: BellRing, colorClass: 'text-emerald-600' },
    recall: { icon: RotateCcw, colorClass: 'text-violet-600' },
    intake_invitation: { icon: ClipboardList, colorClass: 'text-amber-600' },
    intake_confirmation: { icon: ClipboardList, colorClass: 'text-amber-600' },
    invoice: { icon: FileText, colorClass: 'text-sky-600' },
    manual: { icon: Mail, colorClass: 'text-brand-primary' },
    booking_request: { icon: Globe, colorClass: 'text-slate-500' },
    contact_form: { icon: Globe, colorClass: 'text-slate-500' },
    practice_notification: { icon: Building2, colorClass: 'text-slate-500' },
    // Inbound (Phase 2a): Pfeil nach innen, bewusst dezenter slate-Ton.
    inbound_email: { icon: ArrowDownLeft, colorClass: 'text-slate-500' },
};

const STATUS_META: Record<CommunicationDeliveryStatus, { tone: StatusTone; labelKey: string }> = {
    sent: { tone: 'success', labelKey: 'crm.timeline.statusSent' },
    failed: { tone: 'danger', labelKey: 'crm.timeline.statusFailed' },
    sending: { tone: 'neutral', labelKey: 'crm.timeline.statusSending' },
    unknown: { tone: 'warning', labelKey: 'crm.timeline.statusUnknown' },
    skipped: { tone: 'neutral', labelKey: 'crm.timeline.statusSkipped' },
};

// Inbound-Einträge zeigen statt des Zustellstatus den Inbox-Zustand
// (geteilte Labels mit der Posteingang-Seite, Namespace crm.state).
const INBOX_STATE_META: Record<CommunicationInboxState, { tone: StatusTone; labelKey: string }> = {
    new: { tone: 'info', labelKey: 'crm.state.new' },
    read: { tone: 'neutral', labelKey: 'crm.state.read' },
    answered: { tone: 'success', labelKey: 'crm.state.answered' },
    unmatched: { tone: 'warning', labelKey: 'crm.state.unmatched' },
};

const CommunicationTimeline: React.FC<CommunicationTimelineProps> = ({ patientId, patient, can, refreshToken }) => {
    const { t } = useTranslation();
    const { addToast } = useToast();

    const [items, setItems] = useState<Communication[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showInternal, setShowInternal] = useState(false);
    const [selected, setSelected] = useState<Communication | null>(null);
    const [showCompose, setShowCompose] = useState(false);
    // Antwort auf eine Inbound-Mail (öffnet ComposeMailDialog vorbefüllt).
    const [replyTo, setReplyTo] = useState<Communication | null>(null);
    const [resending, setResending] = useState(false);

    const load = async (limit: number) => {
        try {
            setLoading(true);
            setError(null);
            const res = await apiService.listPatientCommunications(patientId, { limit });
            setItems(res.data.data?.items || []);
            setTotal(res.data.data?.total || 0);
        } catch {
            setError(t('crm.timeline.errorLoad'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load(INITIAL_LIMIT);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patientId, refreshToken]);

    // Nach Compose/Resend: neu laden, dabei die aktuelle Listenlänge (+ neuer
    // Eintrag) beibehalten, damit eine ausgeklappte Liste nicht zusammenfällt.
    const reload = () => load(Math.max(INITIAL_LIMIT, items.length + 1));

    // before_id-Pagination: „Alle N Einträge anzeigen" lädt nach.
    const loadMore = async () => {
        const last = items[items.length - 1];
        if (!last || loadingMore) return;
        try {
            setLoadingMore(true);
            const res = await apiService.listPatientCommunications(patientId, { limit: PAGE_LIMIT, before_id: last.id });
            setItems(prev => [...prev, ...(res.data.data?.items || [])]);
            if (typeof res.data.data?.total === 'number') setTotal(res.data.data.total);
        } catch {
            addToast(t('crm.timeline.errorLoad'), 'error');
        } finally {
            setLoadingMore(false);
        }
    };

    const resend = async (comm: Communication) => {
        try {
            setResending(true);
            await apiService.resendCommunication(comm.id);
            addToast(t('crm.detail.resendOk'), 'success');
            setSelected(null);
            await reload();
        } catch (e: any) {
            addToast(e?.response?.data?.message || t('crm.detail.resendFailed'), 'error');
        } finally {
            setResending(false);
        }
    };

    const visible = showInternal ? items : items.filter(c => !INTERNAL_KINDS.has(c.kind));
    const canSend = can('communications');

    const statusPill = (c: Communication) => {
        // Inbound: Inbox-Zustand statt Zustellstatus.
        if (c.direction === 'inbound') {
            const meta = c.inbox_state ? INBOX_STATE_META[c.inbox_state] : null;
            if (!meta) return null;
            return <StatusPill tone={meta.tone} dot>{t(meta.labelKey)}</StatusPill>;
        }
        const meta = STATUS_META[c.delivery_status] || STATUS_META.unknown;
        return <StatusPill tone={meta.tone} dot>{t(meta.labelKey)}</StatusPill>;
    };

    // "Re: "-Präfix nur ergänzen, wenn noch keines da ist.
    const replySubject = (subject: string): string =>
        /^re:/i.test(subject.trim()) ? subject : `Re: ${subject}`;

    return (
        <div className={sectionClass}>
            <div className="flex items-center justify-between gap-3 mb-3">
                <h3 className="text-sm font-semibold text-brand-text flex items-center gap-2">
                    <Mail className="h-4 w-4 text-brand-primary" strokeWidth={1.75} />
                    {t('crm.timeline.title')}
                </h3>
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => setShowInternal(v => !v)}
                        className="text-[11px] font-medium text-brand-text-muted hover:text-brand-text transition-colors cursor-pointer"
                    >
                        {showInternal ? t('crm.timeline.hideInternal') : t('crm.timeline.showInternal')}
                    </button>
                    {canSend && (
                        <button
                            type="button"
                            onClick={() => setShowCompose(true)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded transition-colors cursor-pointer"
                        >
                            <Mail className="h-3 w-3" strokeWidth={2} />
                            {t('crm.timeline.sendEmail')}
                        </button>
                    )}
                </div>
            </div>

            {loading && items.length === 0 ? (
                <p className="text-sm text-brand-text-muted">{t('crm.timeline.loading')}</p>
            ) : error ? (
                <p className="text-sm text-rose-600">{error}</p>
            ) : total === 0 ? (
                <p className="text-sm text-brand-text-muted">{t('crm.timeline.empty')}</p>
            ) : visible.length === 0 ? (
                <p className="text-sm text-brand-text-muted">{t('crm.timeline.emptyFiltered')}</p>
            ) : (
                <ul className="ml-0.5 space-y-0.5 border-l-2 border-brand-border pl-3">
                    {visible.map(c => {
                        const meta = KIND_META[c.kind] || KIND_META.manual;
                        const Icon = meta.icon;
                        // Inbound: Absender statt Empfänger anzeigen.
                        const address = c.direction === 'inbound' ? c.from_address : c.to;
                        return (
                            <li key={c.id}>
                                <button
                                    type="button"
                                    onClick={() => setSelected(c)}
                                    className="w-full flex items-center gap-2.5 px-1.5 py-1.5 text-left rounded-md hover:bg-brand-surface-2 transition-colors cursor-pointer"
                                >
                                    <Icon className={`h-4 w-4 shrink-0 ${meta.colorClass}`} strokeWidth={1.75} />
                                    <span className="min-w-0 flex-1">
                                        <span className="block text-[13px] font-medium text-brand-text truncate">
                                            {c.subject || t('crm.timeline.noSubject')}
                                        </span>
                                        <span className="block text-xs text-brand-text-muted truncate">
                                            <span className="tabular-nums">{formatDateTime(c.sent_at || c.created_at)}</span>
                                            {address && <> · {address}</>}
                                        </span>
                                    </span>
                                    {statusPill(c)}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}

            {/* „Alle anzeigen" bewusst außerhalb der Liste — bleibt auch dann
                erreichbar, wenn alle geladenen Einträge intern (gefiltert) sind. */}
            {!loading && !error && items.length < total && (
                <button
                    type="button"
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="mt-1 ml-0.5 flex items-center gap-1 pl-3 py-1.5 text-xs font-medium text-brand-primary hover:text-brand-primary-hover transition-colors cursor-pointer disabled:opacity-50"
                >
                    <ChevronDown className="h-3.5 w-3.5" strokeWidth={1.75} />
                    {loadingMore ? t('crm.timeline.loading') : t('crm.timeline.showAll', { count: total })}
                </button>
            )}

            {/* Read-only Detail-Modal (Overlay-Muster wie billing/SendInvoiceDialog) */}
            {selected && (
                <div className="fixed inset-0 z-50 flex items-start justify-center pt-12 px-4 bg-black/50 overflow-y-auto" onClick={() => setSelected(null)}>
                    <div className="w-full max-w-2xl rounded-xl bg-brand-surface border border-brand-border shadow-2xl" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-5 py-3 border-b border-brand-border">
                            <div className="flex items-center gap-2 min-w-0">
                                <h3 className="text-sm font-semibold text-brand-text truncate">
                                    {selected.direction === 'inbound' ? t('crm.timeline.inboundLabel') : t('crm.detail.title')}
                                </h3>
                                {statusPill(selected)}
                            </div>
                            <button onClick={() => setSelected(null)} className="p-1 text-brand-text-muted hover:text-brand-text rounded cursor-pointer" aria-label={t('common.close')}>
                                <XIcon className="h-4 w-4" strokeWidth={1.75} />
                            </button>
                        </div>

                        <div className="p-5 space-y-3">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <span className={labelClass}>{t('crm.detail.from')}</span>
                                    <p className="text-sm text-brand-text break-all">
                                        {selected.direction === 'inbound'
                                            ? (selected.from_address || selected.from_name || '—')
                                            : (FROM_ADDRESS[selected.from_account] || selected.from_account)}
                                    </p>
                                </div>
                                <div>
                                    <span className={labelClass}>{t('crm.detail.to')}</span>
                                    <p className="text-sm text-brand-text break-all">{selected.to || '—'}</p>
                                </div>
                                <div>
                                    <span className={labelClass}>{t('crm.detail.subject')}</span>
                                    <p className="text-sm text-brand-text">{selected.subject || t('crm.timeline.noSubject')}</p>
                                </div>
                                <div>
                                    <span className={labelClass}>{t('crm.detail.time')}</span>
                                    <p className="text-sm text-brand-text tabular-nums">{formatDateTime(selected.sent_at || selected.created_at)}</p>
                                </div>
                            </div>

                            {selected.delivery_status === 'failed' && selected.error && (
                                <div className="px-3 py-2 bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 rounded-md text-xs text-rose-700 dark:text-rose-300">
                                    <span className="font-semibold">{t('crm.detail.error')}:</span> {selected.error}
                                </div>
                            )}

                            {selected.body_text ? (
                                <pre className="whitespace-pre-wrap max-h-72 overflow-y-auto px-3 py-2 bg-brand-surface-2/60 border border-brand-border rounded-md text-[12.5px] font-mono leading-relaxed text-brand-text">
                                    {selected.body_text}
                                </pre>
                            ) : (
                                <p className="px-3 py-2 bg-brand-surface-2/60 border border-brand-border rounded-md text-xs italic text-brand-text-muted">
                                    {t('crm.detail.bodyNotArchived')}
                                </p>
                            )}

                            <p className="text-[11px] text-brand-text-muted break-all">
                                {selected.message_id && <>{t('crm.detail.messageId')}: {selected.message_id} · </>}
                                {t('crm.detail.trigger')}: {selected.trigger} / {selected.sent_by}
                            </p>
                        </div>

                        <div className="px-5 py-3 border-t border-brand-border flex items-center justify-between gap-2">
                            <div>
                                {selected.kind === 'invoice' && selected.ref_id != null && (
                                    <Link
                                        to={`/admin/invoices/${selected.ref_id}`}
                                        className="inline-flex items-center gap-1 text-xs font-medium text-brand-primary hover:text-brand-primary-hover transition-colors"
                                    >
                                        <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.75} />
                                        {t('crm.detail.toInvoice')}
                                    </Link>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                {canSend && selected.direction === 'inbound' && (
                                    <button
                                        type="button"
                                        onClick={() => { setReplyTo(selected); setSelected(null); }}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md transition-colors cursor-pointer"
                                    >
                                        <Send className="h-3.5 w-3.5" strokeWidth={2} />
                                        {t('crm.detail.reply')}
                                    </button>
                                )}
                                {canSend && selected.kind === 'manual' && selected.delivery_status === 'failed' && (
                                    <button
                                        type="button"
                                        onClick={() => resend(selected)}
                                        disabled={resending}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md transition-colors cursor-pointer disabled:opacity-50"
                                    >
                                        <Send className="h-3.5 w-3.5" strokeWidth={2} />
                                        {resending ? t('crm.detail.resending') : t('crm.detail.resend')}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => setSelected(null)}
                                    className="px-3 py-1.5 text-xs font-medium text-brand-text-muted hover:bg-brand-surface-2 rounded cursor-pointer"
                                >
                                    {t('common.close')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showCompose && (
                <ComposeMailDialog
                    patient={patient}
                    onClose={() => setShowCompose(false)}
                    onSent={reload}
                />
            )}

            {/* Antwort auf eine Inbound-Mail — hängt die Antwort in den Thread
                und markiert die Inbound-Mail serverseitig als 'answered'. */}
            {replyTo && (
                <ComposeMailDialog
                    patient={patient}
                    replyToCommunicationId={replyTo.id}
                    initialTo={replyTo.from_address || patient.email || ''}
                    initialSubject={replySubject(replyTo.subject || '')}
                    onClose={() => setReplyTo(null)}
                    onSent={reload}
                />
            )}
        </div>
    );
};

export default CommunicationTimeline;
